import { classNames } from "../lib/format";

export type TabGroup = {
  title: string;
  items: TabItem[];
};

export type TabItem = {
  id: string;
  label: string;
  description?: string;
  badge?: string;
};

type Props = {
  groups: TabGroup[];
  activeId: string;
  onChange: (id: string) => void;
  connection: {
    bff: "ok" | "error" | "unknown";
    upstream: "ok" | "error" | "unknown";
    upstreamUrl?: string;
  };
};

const dotColor: Record<"ok" | "error" | "unknown", string> = {
  ok: "bg-brand-500",
  error: "bg-rose-500",
  unknown: "bg-ink-300",
};

export function Sidebar({ groups, activeId, onChange, connection }: Props) {
  return (
    <aside className="ks-scroll flex h-full w-72 shrink-0 flex-col overflow-y-auto border-r border-ink-200 bg-white">
      <header className="px-4 py-5">
        <div className="flex items-center gap-2">
          <div className="grid size-9 place-content-center rounded-lg bg-brand-600 text-white">
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="6" cy="18" r="2" />
              <circle cx="18" cy="6" r="2" />
              <path d="M6 18 L11 9 L15 13 L18 6" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight text-ink-900">RouteCore</p>
            <p className="text-[11px] uppercase tracking-wider text-ink-500">Kitchen Sink</p>
          </div>
        </div>
      </header>

      <nav className="flex flex-1 flex-col gap-5 px-2 pb-6">
        {groups.map((group) => (
          <div key={group.title}>
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
              {group.title}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = item.id === activeId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onChange(item.id)}
                      className={classNames(
                        "group flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm transition",
                        active
                          ? "bg-brand-50 text-brand-800"
                          : "text-ink-700 hover:bg-ink-100",
                      )}
                    >
                      <span
                        className={classNames(
                          "mt-1 inline-block size-1.5 rounded-full",
                          active ? "bg-brand-600" : "bg-ink-300 group-hover:bg-ink-400",
                        )}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className={active ? "font-semibold" : "font-medium"}>
                            {item.label}
                          </span>
                          {item.badge && (
                            <span className="ks-tag bg-ink-100 text-[10px] text-ink-600">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-[11px] leading-snug text-ink-500">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <footer className="border-t border-ink-200 bg-ink-50/60 px-4 py-3 text-[11px] text-ink-600">
        <div className="flex items-center gap-2">
          <span className={classNames("size-2 rounded-full", dotColor[connection.bff])} />
          <span>BFF Go</span>
          <span className="ml-auto text-ink-500">
            {connection.bff === "ok" ? "conectado" : connection.bff === "error" ? "offline" : "—"}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className={classNames("size-2 rounded-full", dotColor[connection.upstream])} />
          <span>RouteCore</span>
          <span className="ml-auto text-ink-500">
            {connection.upstream === "ok"
              ? "conectado"
              : connection.upstream === "error"
                ? "offline"
                : "—"}
          </span>
        </div>
        {connection.upstreamUrl && (
          <p className="mt-2 truncate text-[10px] text-ink-400">{connection.upstreamUrl}</p>
        )}
      </footer>
    </aside>
  );
}
